import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Shield, Globe, Server, BarChart3, Wallet, Building, Layers, Zap } from 'lucide-react';

const OffreFOGEDOM = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-500 rounded-xl flex items-center justify-center">
              <Building className="w-6 h-6 text-slate-900" />
            </div>
            <span className="font-bold text-xl text-white">FOGEDOM</span>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/offre-coppam" className="text-slate-400 hover:text-cyan-400 transition">COPPAM</Link>
            <Link to="/offre-oscop" className="text-slate-400 hover:text-cyan-400 transition">O'SCOP</Link>
            <Link to="/login" className="bg-cyan-500 text-slate-900 px-5 py-2.5 rounded-xl font-medium hover:bg-cyan-400 transition">
              Connexion
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-cyan-500/20 text-cyan-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-cyan-500/30">
            <Globe className="w-4 h-4" />
            Déploiement à grande échelle
          </span>
          <h1 className="text-5xl font-bold text-white mb-4">
            Offre <span className="text-cyan-400">Institution & Réseaux</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Déploiement multi-territoires des dispositifs solidaires avec pilotage d'impact.
            Fédérations, collectivités, partenaires institutionnels.
          </p>
        </div>

        {/* Main Card */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-slate-800/50 backdrop-blur rounded-3xl border border-slate-700 overflow-hidden">
            {/* Price Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full text-white text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  Enterprise
                </div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-7xl font-bold text-white">199</span>
                  <span className="text-2xl text-cyan-100">€ /mois</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur px-5 py-2.5 rounded-full">
                  <Wallet className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">350 crédits inclus / mois</span>
                </div>
                <p className="text-cyan-100 text-sm mt-3">Soit 0,57 € / crédit inclus</p>
              </div>
            </div>

            {/* Features Grid */}
            <div className="p-10">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div>
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-cyan-400" />
                    Accès complet
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Tous les simulateurs COPPAM',
                      'Personnalisations avancées',
                      'Avenants illimités',
                      'Multi-structures / filiales',
                      'Gestion des territoires'
                    ].map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-cyan-400" />
                        </div>
                        <span className="text-slate-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right Column */}
                <div>
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Server className="w-5 h-5 text-cyan-400" />
                    Fonctionnalités Enterprise
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'API REST complète',
                      'Marque blanche',
                      'Traitements batch',
                      'Reporting ESS avancé',
                      'Account manager dédié'
                    ].map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-cyan-400" />
                        </div>
                        <span className="text-slate-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Pack institutionnel */}
              <div className="mt-8 p-6 bg-slate-700/50 rounded-2xl border border-slate-600">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-white">Pack Institutionnel</h4>
                    <p className="text-slate-400 text-sm">500 crédits supplémentaires</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-cyan-400">200 €</div>
                    <div className="text-xs text-slate-500">0,40 € / crédit</div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Link 
                to="/inscription-ess?plan=institution"
                className="mt-8 w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 py-4 px-6 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-cyan-500/30"
              >
                <Shield className="w-5 h-5" />
                Demander un accès institutionnel
              </Link>
            </div>
          </div>
        </div>

        {/* Impact Section */}
        <div className="max-w-4xl mx-auto mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: BarChart3, title: 'Reporting ESS', desc: 'Indicateurs d\'impact exportables' },
            { icon: Globe, title: 'Multi-territoires', desc: 'DOM-TOM, Métropole, International' },
            { icon: Shield, title: 'Conformité', desc: 'Traçabilité probante intégrée' }
          ].map((item, i) => (
            <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700 text-center">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <item.icon className="w-6 h-6 text-cyan-400" />
              </div>
              <h4 className="font-semibold text-white mb-1">{item.title}</h4>
              <p className="text-slate-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Legal Note */}
        <div className="text-center mt-12">
          <p className="text-sm text-slate-500 max-w-3xl mx-auto">
            <strong className="text-slate-400">Mention légale :</strong> FOGEDOM intervient comme fonds de prévention 
            et de garantie solidaire. Aucun encaissement d'abonnements – les abonnements sont gérés par O'SCOP.
            Aucune activité d'assurance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OffreFOGEDOM;
